import { faFileExport } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress, Tooltip, Typography } from '@mui/material';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '@/util/Configuracao';
import SystemUtil from '@/util/SystemUtil';
import NumeroProcessoUtil from '../../util/NumeroProcessoUtil';
import ReactComponent from '../ReactComponent';
import { ref } from './FerramentasListaProcessos';
import ExportarProcessosParaArquivoCsv from './ExportarProcessosParaArquivoCsv';
import DataUtil from '@/util/DataUtil';

class ExportarProcessosParaArquivoCsvPauta extends ReactComponent {
    /**
     * @desc Separador CSV para o arquivo a ser exportado.
     * @static
     * @readonly
     * @constant {string}
     */
    static SEPARADOR_CSV = ';';

    gerandoArquivo = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            gerandoArquivo: observable,
            handleClickExportarLista: action,
            nomeArquivoCsv: computed,
            dados: computed,
            conteudoCsv: computed,
            temFiltroAtivo: computed
        });
    }

    /**
     * @desc Exporta a lista.
     */
    handleClickExportarLista = async () => {
        this.gerandoArquivo = true;
        SystemUtil.exportarAquivoCsv(this.nomeArquivoCsv, this.conteudoCsv);
        this.gerandoArquivo = false;
    };

    /**
     * @desc Monta e retorna o nome do arquivo CSV.
     *
     * @returns {string} O nome do arquivo CSV.
     */
    get nomeArquivoCsv() {
        let descricaoPauta = '';

        if (this.pautaJulgamentoStore?.pautaSelecionada?.descricao) {
            descricaoPauta = '_' + this.pautaJulgamentoStore.pautaSelecionada.descricao.replace(/\s/g, '-');
        }

        return this.sessaoStore?.montarNomeArquivoExportacao(`processos_pauta${descricaoPauta}`);
    }

    /**
     * @desc Monta os dados para exportação.
     */
    get dados() {
        let processosSelecionados = []
            .concat(this.pautaJulgamentoStore.processosPautaSelecionadaFiltrados)
            .concat(this.pautaJulgamentoStore.processosDisponiveisFiltrados);

        return processosSelecionados.map(function (processo) {
            let regional = ExportarProcessosParaArquivoCsvPauta.extrairTribunalDoNumeroProcesso(
                processo.processo.numeroCompleto
            );

            return {
                numeroCompleto:
                    processo.processo.origem === Configuracao.ORIGEM_PJE
                        ? NumeroProcessoUtil.formatarNumeroProcesso(processo.processo.numeroCompleto, true, true)
                        : processo.processo.numeroCompleto,
                relatorSigla: processo?.processo?.relator?.sigla || null,
                composicao: processo.composicao
                    ? processo.composicao.map((comp) => comp.magistrado.sigla).join(', ')
                    : null,
                vistor:
                    Array.isArray(processo?.informacoesAdicionais?.RetornoVistaRegimental) &&
                    processo.informacoesAdicionais.RetornoVistaRegimental.length > 0
                        ? processo.informacoesAdicionais.RetornoVistaRegimental.map((rvr) => rvr.magistrado.sigla).join(
                              ', '
                          )
                        : null,
                impedimentosPossiveis:
                    processo?.impedimentos &&
                    processo.impedimentos
                        .filter((impedimento) => impedimento.situacaoImpedimento === 'IMPEDIMENTO_POSSIVEL')
                        .map((impedimento) => impedimento.magistrado.sigla)
                        .join(', '),
                impedimentosDeclarados:
                    processo?.impedimentos &&
                    processo.impedimentos
                        .filter((impedimento) => impedimento.situacaoImpedimento === 'IMPEDIMENTO_DECLARADO')
                        .map((impedimento) => impedimento.magistrado.sigla)
                        .join(', '),
                segredoJustica: processo.segredoJustica ? 'Sim' : '',
                peticoesPendentes:
                    processo.informacoesAdicionais.QuantidadePeticoesPendentes > 0
                        ? processo.informacoesAdicionais.QuantidadePeticoesPendentes
                        : null,
                trt: regional,
                situacao: ExportarProcessosParaArquivoCsvPauta.getDescricaoSituacao(processo),
                ultimaLiberacaoPautaEm: DataUtil.formatarDataHoraParaCsv(processo.processo.ultimaLiberacaoPautaEm)
            };
        });
    }

    /**
     * @desc Monta os cabeçalhos do CSV.
     */
    get cabecalhos() {
        let cabecalhos = [];

        cabecalhos.push(
            { label: 'Número Processo', key: 'numeroCompleto' },
            { label: 'Relator', key: 'relatorSigla' },
            { label: 'Composição', key: 'composicao' },
            { label: 'Retorno Vista Reg.', key: 'vistor' },
            { label: 'Possíveis Impedimentos', key: 'impedimentosPossiveis' },
            { label: 'Impedimentos Declarados', key: 'impedimentosDeclarados' },
            { label: 'Segredo de Justiça', key: 'segredoJustica' },
            { label: 'Petições Pendentes', key: 'peticoesPendentes' },
            { label: 'Tribunal', key: 'trt' },
            { label: 'Situação', key: 'situacao' },
            { label: 'Última Liberação Pauta', key: 'ultimaLiberacaoPautaEm' }
        );

        return cabecalhos;
    }

    /**
     * @desc Extrai o tribunal a partir do número do processo.
     */
    static extrairTribunalDoNumeroProcesso(numeroProcesso) {
        if (numeroProcesso) {
            const numeroProcessoObj = NumeroProcessoUtil.getObjetoNumeroProcesso(numeroProcesso);
            return numeroProcessoObj.regional;
        }
        return '';
    }

    /**
     * @desc Retorna a descrição da situação do processo.
     */
    static getDescricaoSituacao(registro) {
        if (registro.inclusaoNaPauta) {
            let descricao = 'Em pauta/sessão - ';

            if (registro.confirmadaInclusaoPauta) {
                descricao += 'Confirmado';
            } else {
                descricao += 'Não confirmado';
            }

            return descricao;
        }

        return 'Disponível para inclusão em pauta/sessão';
    }

    /**
     * @desc Monta o conteúdo do CSV.
     */
    get conteudoCsv() {
        const sep = ExportarProcessosParaArquivoCsvPauta.SEPARADOR_CSV;
        const cabecalhoLinha = this.cabecalhos.map((col) => col.label).join(sep);
        const linhas = this.dados.map((linha) =>
            this.cabecalhos.map((col) => `"${(linha[col.key] ?? '').toString().replace(/"/g, '""')}"`).join(sep)
        );
        return [cabecalhoLinha, ...linhas].join('\n');
    }

    get temFiltroAtivo() {
        return Array.isArray(this.store.filtros?.filtrosComValor) && this.store.filtros.filtrosComValor.length > 0;
    }

    render() {
        if (this.gerandoArquivo) {
            return <CircularProgress size="24px" />;
        }

        const filtrados = this.temFiltroAtivo ? ' filtrados' : '';

        return (
            <div>
                <Tooltip
                    onClick={this.handleClickExportarLista}
                    title={
                        <Typography>
                            {`Exportar listas de processos${filtrados} (da pauta e disponíveis) para arquivo CSV`}
                        </Typography>
                    }
                    placement="top-start"
                    ref={ref}
                    style={{ marginLeft: '5px', marginBottom: '5px' }}
                >
                    <div
                        className="corBotao"
                        aria-label={`Exportar listas de processos${filtrados} para arquivo CSV`}
                    >
                        <FontAwesomeIcon
                            icon={faFileExport}
                            style={{ cursor: 'pointer', width: '22px', height: '22px', marginRight: '10px' }}
                        />
                    </div>
                </Tooltip>
            </div>
        );
    }
}

export default inject('stores')(observer(ExportarProcessosParaArquivoCsvPauta));
